import React, { useEffect, useMemo, useState, useRef } from 'react'
import useShellRootContext from '../contexts/ShellRoot/useShellRootContext'

const OD_KEY_MAPPING_DEVICE = {
  modelName: 'modelName',
  bizModel: 'bizModel',
  highestResImgUrl: 'printerImage'
}

const OD_KEY_MAPPING_USER = {
  selectedDeviceLocation: 'location',
  selectedDeviceName: 'name'
}

const defaultAvatar = {
  bizModel: null,
  printerImage: null,
  name: null,
  modelName: null,
  location: null,
  organization: null,
  programLevel: null
}

export const ConfigContext = React.createContext({
  country: null,
  language: null,
  avatarData: defaultAvatar,
  sessionContext: {},
  stageContext: {},
  customSidebar: null,
  sidebar: null,
  experience: null,
  stepperClickHandler: null,
  getText: (key) => key,
  getTextTree: (key) => ({ text: key }),
  isHpPlus: false,
  isEcpExperience: false,
  isLfp: false
})

const FALLBACK_SIDEBAR = {
  customSidebar: null,
  experience: null,
  stepperClickHandler: null
}

const ConfigProvider = (props) => {
  const {
    sidebarProps = { sidebar: FALLBACK_SIDEBAR },
    store = null,
    localization = { country: 'us', language: 'en' }
  } = props
  const { t } = useShellRootContext()

  localization.country = localization.country.toUpperCase()
  const { country, language } = localization
  const { sidebar } = sidebarProps
  const { customSidebar, experience, stepperClickHandler } =
    sidebar || FALLBACK_SIDEBAR

  const [avatarData, setAvatarData] = useState(defaultAvatar)
  const [sessionContext, setSessionContext] = useState({})
  const prevSessionContextRef = useRef()

  const isHpPlus = useMemo(
    () => avatarData?.programLevel?.toLowerCase?.() === 'hpplus',
    [avatarData]
  )

  const isEcpExperience = useMemo(
    () =>
      sessionContext?.onboardingContext?.experience?.toLowerCase?.() === 'ecp',
    [sessionContext]
  )

  const isLfp = useMemo(
    () =>
      sessionContext?.onboardingContext?.productFamily?.toUpperCase?.() ===
      'LFP_DUNE',
    [sessionContext]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateAvatarWithSessionData = () => {
    setAvatarData((prevAvatarData) => {
      const updates = {}

      const mapFields = (source, mapping) => {
        if (!source) return
        Object.entries(mapping).forEach(([sourceKey, targetKey]) => {
          const value = source[sourceKey]
          if (value !== null && prevAvatarData[targetKey] !== value) {
            updates[targetKey] = value
          }
        })
      }

      if (
        sessionContext?.onboardingContext?.programLevel &&
        !prevAvatarData.programLevel
      ) {
        updates.programLevel = sessionContext.onboardingContext.programLevel
      }

      mapFields(sessionContext?.device, OD_KEY_MAPPING_DEVICE)
      mapFields(sessionContext?.user, OD_KEY_MAPPING_USER)

      if (
        sessionContext?.tenant?.accountName &&
        prevAvatarData.organization !== sessionContext.tenant.accountName
      ) {
        updates.organization = sessionContext.tenant.accountName
      }

      return Object.keys(updates).length > 0
        ? { ...prevAvatarData, ...updates }
        : prevAvatarData
    })
  }

  useEffect(() => {
    const newContext = store?.state?.onboarding?.sessionContext
    if (
      newContext &&
      JSON.stringify(prevSessionContextRef.current) !==
        JSON.stringify(newContext)
    ) {
      prevSessionContextRef.current = newContext
      setSessionContext(newContext)
    }
  }, [store?.state?.onboarding?.sessionContext])

  useEffect(() => {
    updateAvatarWithSessionData()
  }, [sessionContext, updateAvatarWithSessionData])

  const configState = {
    country,
    language,
    avatarData,
    sessionContext,
    customSidebar,
    stepperClickHandler,
    experience,
    sidebarProps,
    isHpPlus,
    isEcpExperience,
    isLfp,
    getText: t,
    getTextTree: (key) => t(key, { returnObjects: true })
  }

  return (
    <ConfigContext.Provider value={configState}>
      {props.children}
    </ConfigContext.Provider>
  )
}

export default ConfigProvider
